import React, { useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI';
import IncidentProcessedByInitialReviewTable from './Table/IncidentProcessedByInitialReview';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { useSelector } from 'react-redux';
import toasters from '../../../../../../../utils/toasters/Toasters';
import { useSearchParams } from 'react-router-dom'

const formatPayloadDates = (payload) => {
    if (!payload) return payload;
  
    const formattedPayload = { ...payload };
    
    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate);
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0];
        }
    }
  
    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate);
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0];
        }
    }
  
    return formattedPayload;
};

const IncidentProcessedByInitialReview = () => {
    const location = useLocation();
    const { token } = useSelector((state) => state?.auth);
    const [sort, setSort] = useState(null);
 
   const [searchParams] = useSearchParams();
const [payload, setPayload] = useState(null);

useEffect(() => {

    // Try reading from URL params
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const code = searchParams.get('code');

    if (startDate || endDate) {
        const urlPayload = { startDate, endDate, code };
        setPayload(urlPayload);
        sessionStorage.setItem('incidentByInitialReviewPayload', JSON.stringify(urlPayload));
        return;
    }

    // fallback to location.state
    if (location.state) {
        setPayload(location.state);
        sessionStorage.setItem('incidentByInitialReviewPayload', JSON.stringify(location.state));
        return;
    }

    // fallback to sessionStorage
    const storedPayload = sessionStorage.getItem('incidentByInitialReviewPayload');
    if (storedPayload) {
        setPayload(JSON.parse(storedPayload));
    }

}, [location.state, searchParams]);

const formattedPayload = formatPayloadDates(payload);

    const getIncidentProcessedByInitialReview = useCallback(async () => {
        let apiQueryParams = {};
        if (sort && sort?.columnName && sort?.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort?.columnName,
            };
        }

        return fetchIncidentAnalysisData({ ...formattedPayload, ...apiQueryParams, export: 'N' });
    }, [sort, formattedPayload]);

    const handleColumnSort = async (column, sortDirection) => {
        setSort((prev) => ({
            ...prev,
            columnName: column?.sortField,
            order: sortDirection,
        }));
    };

    const {
        data: IncidentProcessedByInitialReviewData,
        isLoading,
      } = useQuery(
        ['incident-processed-by-initial-review', formattedPayload, sort],
        getIncidentProcessedByInitialReview,
        {
            enabled: !!formattedPayload,
          select: (data) => {
            const totalSentToLocal = data?.reduce((sum, item) => sum + item?.sent_to_local, 0);
            const totalScoreAdjusted = data?.reduce((sum, item) => sum + item?.score_adjusted, 0);
            const Total = data?.reduce((sum, item) => sum + item?.total, 0);
      
            const obj = {
              name_tx: 'Total',
              sent_to_local: totalSentToLocal || 0,
              score_adjusted: totalScoreAdjusted || 0,
              total: Total || 0,
            };
      
            return [...data, obj];
          },
          retry: (failureCount, error) => {
            const status = error?.response?.status;
            return failureCount < 1 && status >= 400 && status < 600; 
          },
          retryDelay: 1000, 
        }
      );
      

    // PDF download function
   const downloadPDF = () => {
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [297, 210],
    });

    doc.setFontSize(18);
    doc.text('Incident Processed By Initial Review Report', 14, 22);

    doc.setFontSize(12);
    doc.text(
        `Report Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
        14,
        30
    );

    const tableColumn = ['Full Name', 'Send To local', 'Adjusted Score', 'Total'];
    const tableRows = IncidentProcessedByInitialReviewData?.map((item) => [
        item?.name_tx || '',
        item?.sent_to_local || '',
        item?.score_adjusted || '',
        item?.total || 0,
    ]) || [];

    doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        theme: 'grid',
        // 1. Force the table to span from the left margin to the right margin
        margin: { left: 14, right: 14 },
        tableWidth: 'auto', 
        
        headStyles: {
            fillColor: [0, 81, 242],
            textColor: [255, 255, 255],
            fontSize: 12,
            halign: 'center', // Centering headers looks better on wide tables
        },
        bodyStyles: {
            fontSize: 10,
            cellPadding: 4,
            halign: 'center', // Center data for a cleaner look
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245],
        },
        styles: {
            lineColor: [0, 0, 0],
            lineWidth: 0.2, // Thinner lines look more professional in landscape
        },
        // 2. Remove fixed widths to allow the engine to distribute space
        columnStyles: {
            0: { halign: 'left' }, // Keep names left-aligned for readability
            1: { cellWidth: 'auto' },
            2: { cellWidth: 'auto' },
            3: { cellWidth: 'auto' },
        },
    });

    doc.save('incident-processed-by-initial-review-report.pdf');
};

    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    };

    const ExcelIncidentByInitialReview = async () => {
        try {
            let response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                { ...formattedPayload, export: 'Y' },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );
            downloadFile(response?.data, 'incidents processed by initial review');
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message });
        }
    };

    return (
        <>
            <IncidentProcessedByInitialReviewTable
                DisplayDate={formattedPayload}
                IncidentProcessedByInitialReviewData={IncidentProcessedByInitialReviewData}
                handleColumnSort={handleColumnSort}
                downloadPDF={downloadPDF}
                downloadExcel={ExcelIncidentByInitialReview}
                isLoading={isLoading}
                reportData={formattedPayload}
                reportCode={payload?.code || 'incidentProcessedByInitialReview'}
                reportTableData={IncidentProcessedByInitialReviewData}
                generatePDFFunction={downloadPDF}
            />
        </>
    );
};

export default IncidentProcessedByInitialReview;
